/**
 * SuggestionsContext — manages AI suggestion state for document editing.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {debounce} from 'lodash';
import {useParams} from 'react-router-dom';
import {getAiToolkit} from '@tiptap-pro/ai-toolkit';
import ExitProofreadingDialog from '../components/report-workspace/ExitProofreadingDialog';
import {useNavigationGuard} from './NavigationGuardContext';
import {trackProductEvent} from '../util';

const SuggestionsContext = createContext(null);

/**
 * Navigate to a suggestion: set cursor to range.from, focus, then scroll
 * the closest diff widget into view.
 */
const focusAndScrollToSuggestion = (suggestion) => {
  if (!suggestion?.editor || suggestion.editor.isDestroyed) return;

  const {editor} = suggestion;
  editor.commands.setTextSelection(suggestion.range.from);
  editor.commands.focus();

  requestAnimationFrame(() => {
    if (editor.isDestroyed) return;
    try {
      const widgets = editor.view.dom.querySelectorAll(
        '.tiptap-ai-suggestion-diff'
      );
      if (!widgets.length) return;

      // Use coordsAtPos to get pixel coords for the suggestion position
      const coords = editor.view.coordsAtPos(suggestion.range.from);
      let target = null;
      let closest = Infinity;
      for (const w of Array.from(widgets)) {
        const rect = w.getBoundingClientRect();
        const d = Math.abs(rect.top - coords.top);
        if (d < closest) {
          closest = d;
          target = w;
        }
      }

      (target || widgets[0]).scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } catch {
      // Ignore scroll errors
    }
  });
};

/**
 * SuggestionsProvider manages AI suggestion state for document editing.
 * Used by both the proofreading toolbar and the agent chat drawer.
 */
export const SuggestionsProvider = ({children}) => {
  const {documentId} = useParams();
  const [suggestions, setSuggestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acceptCount, setAcceptCount] = useState(0);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);

  const editorsRef = useRef(null);
  const acceptStackRef = useRef([]); // stack of editor refs for undo
  const pendingActionRef = useRef(null);
  // Refs for values accessed inside event listeners (avoids stale closures)
  const suggestionsRef = useRef(suggestions);
  const currentIndexRef = useRef(currentIndex); // updated in updateIndex
  suggestionsRef.current = suggestions;

  // Update currentIndex in both ref (sync reads) and state (UI).
  const updateIndex = useCallback((newIndex) => {
    currentIndexRef.current = newIndex;
    setCurrentIndex(newIndex);
  }, []);

  const hasActiveChanges = suggestions.length > 0;
  const canUndo = acceptCount > 0;

  /** Collect all suggestions from all registered editors. */
  const collectSuggestions = useCallback((editors) => {
    const all = [];
    Object.entries(editors || {}).forEach(([sectionId, editor]) => {
      if (!editor || editor.isDestroyed) return;
      try {
        const toolkit = getAiToolkit(editor);
        toolkit.getSuggestions().forEach((s) => {
          all.push({...s, sectionId, editor});
        });
      } catch {}
    });
    return all;
  }, []);

  /**
   * After accept/reject, re-collect suggestions from the toolkit,
   * clamp the index, and navigate to the new current suggestion.
   */
  const syncAfterAction = useCallback(
    (prevIndex) => {
      const remaining = collectSuggestions(editorsRef.current || {});
      setSuggestions(remaining);

      if (remaining.length === 0) {
        updateIndex(0);
        return;
      }

      const newIndex = Math.min(prevIndex, remaining.length - 1);
      updateIndex(newIndex);

      // Navigate to the new current suggestion after DOM update
      requestAnimationFrame(() => {
        const target = remaining[newIndex];
        if (target) focusAndScrollToSuggestion(target);
      });
    },
    [collectSuggestions, updateIndex]
  );

  /**
   * Refresh suggestions from editors.
   */
  const refreshSuggestions = useCallback(
    (editors) => {
      if (!editors) return;
      editorsRef.current = editors;

      const all = collectSuggestions(editors);
      const isFirstLoad = suggestionsRef.current.length === 0;
      setSuggestions(all);

      // Only reset index on first load
      if (isFirstLoad && all.length > 0) {
        updateIndex(0);
        setTimeout(() => {
          const s = all[0];
          if (s) focusAndScrollToSuggestion(s);
        }, 150);
      }
    },
    [collectSuggestions, updateIndex]
  );

  const goToPrevious = useCallback(() => {
    const list = suggestionsRef.current;
    if (!list.length) return;
    const newIdx = Math.max(0, currentIndexRef.current - 1);
    updateIndex(newIdx);
    focusAndScrollToSuggestion(list[newIdx]);
  }, [updateIndex]);

  const goToNext = useCallback(() => {
    const list = suggestionsRef.current;
    if (!list.length) return;
    const newIdx = Math.min(list.length - 1, currentIndexRef.current + 1);
    updateIndex(newIdx);
    focusAndScrollToSuggestion(list[newIdx]);
  }, [updateIndex]);

  const acceptCurrent = useCallback(() => {
    const slist = suggestionsRef.current;
    const idx = currentIndexRef.current;
    if (!slist.length) return;

    const s = slist[idx];
    if (!s?.editor || s.editor.isDestroyed) return;

    try {
      const toolkit = getAiToolkit(s.editor);

      // Snapshot toolkit suggestions BEFORE accept (for undo restoration)
      const suggestionsBefore = toolkit.getSuggestions();

      const result = toolkit.acceptSuggestion(s.id);

      acceptStackRef.current.push({
        editor: s.editor,
        suggestionsBefore,
        docAfter: result.docAfter,
      });
      setAcceptCount((c) => c + 1);
      trackProductEvent('Suggestion Accepted', {
        document_id: documentId,
        suggestion_index: idx,
        total_suggestions: slist.length,
      });
    } catch (err) {
      console.error('[SuggestionsContext] acceptCurrent error:', err);
    }

    syncAfterAction(idx);
  }, [syncAfterAction, documentId]);

  const rejectCurrent = useCallback(() => {
    const slist = suggestionsRef.current;
    const idx = currentIndexRef.current;
    if (!slist.length) return;

    const s = slist[idx];
    if (!s?.editor || s.editor.isDestroyed) return;

    try {
      const toolkit = getAiToolkit(s.editor);
      toolkit.rejectSuggestion(s.id);
      trackProductEvent('Suggestion Rejected', {
        document_id: documentId,
        suggestion_index: idx,
        total_suggestions: slist.length,
      });
    } catch (err) {
      console.error('[SuggestionsContext] rejectCurrent error:', err);
    }

    syncAfterAction(idx);
  }, [syncAfterAction, documentId]);

  /** Accept all suggestions in all editors. */
  const acceptAll = useCallback(() => {
    const editors = editorsRef.current;
    if (!editors) return;

    // Snapshot toolkit suggestions per editor BEFORE accepting (for undo restoration)
    const editorsWithSuggestions = new Set(
      suggestionsRef.current
        .filter((s) => s.editor && !s.editor.isDestroyed)
        .map((s) => s.editor)
    );

    const undoEntries = [];
    editorsWithSuggestions.forEach((editor) => {
      const toolkit = getAiToolkit(editor);
      undoEntries.push({
        editor,
        suggestionsBefore: toolkit.getSuggestions(),
      });
    });

    // Accept all
    editorsWithSuggestions.forEach((editor) => {
      try {
        const result = getAiToolkit(editor).acceptAllSuggestions();
        const entry = undoEntries.find((e) => e.editor === editor);
        if (entry) entry.docAfter = result.docAfter;
      } catch (err) {
        console.error('[SuggestionsContext] acceptAll error:', err);
      }
    });

    // Push entries after accepting (so stack order is correct for undo)
    undoEntries.forEach((entry) => acceptStackRef.current.push(entry));
    setAcceptCount(acceptStackRef.current.length);
    trackProductEvent('All Suggestions Accepted', {
      document_id: documentId,
      total_suggestions: suggestionsRef.current.length,
    });
    setSuggestions([]);
    updateIndex(0);
  }, [updateIndex, documentId]);

  /** Reject all suggestions in all editors. */
  const rejectAll = useCallback(() => {
    const editors = editorsRef.current;
    if (!editors) return;

    const count = suggestionsRef.current.length;
    Object.values(editors).forEach((editor) => {
      if (!editor || editor.isDestroyed) return;
      try {
        getAiToolkit(editor).rejectAllSuggestions();
      } catch (err) {
        console.error('[SuggestionsContext] rejectAll error:', err);
      }
    });

    trackProductEvent('All Suggestions Rejected', {
      document_id: documentId,
      total_suggestions: count,
    });
    setSuggestions([]);
    updateIndex(0);
  }, [updateIndex, documentId]);

  /**
   * Undo the last accepted suggestion.
   * Currently not getting used anywhere.
   */
  const undoLastAction = useCallback(() => {
    if (!acceptStackRef.current.length) return false;

    const entry = acceptStackRef.current[acceptStackRef.current.length - 1];
    const {editor, suggestionsBefore, docAfter} = entry;

    if (!editor || editor.isDestroyed) {
      acceptStackRef.current.pop();
      setAcceptCount(acceptStackRef.current.length);
      return false;
    }

    // Only undo if the accept is the latest operation (no edits after it)
    if (!editor.state.doc.eq(docAfter)) return false;
    if (!editor.can().undo()) return false;

    acceptStackRef.current.pop();
    setAcceptCount(acceptStackRef.current.length);

    editor.chain().focus().undo().run();

    // Restore suggestion decorations for this editor
    try {
      const toolkit = getAiToolkit(editor);
      toolkit.setSuggestions(suggestionsBefore);
    } catch (err) {
      console.error(
        '[SuggestionsContext] setSuggestions after undo error:',
        err
      );
    }

    // Re-collect from all editors to sync React state with toolkit
    const remaining = collectSuggestions(editorsRef.current || {});
    setSuggestions(remaining);

    const newIndex = Math.min(
      currentIndexRef.current,
      Math.max(0, remaining.length - 1)
    );
    updateIndex(newIndex);
    return true;
  }, [collectSuggestions, updateIndex]);

  /**
   * Clear all suggestions and reset state.
   * @param {boolean} revert - If true, reject suggestions on live editors
   */
  const clearSuggestions = useCallback(
    (revert = true) => {
      if (revert) {
        const editors = editorsRef.current;
        if (editors) {
          Object.values(editors).forEach((editor) => {
            if (!editor || editor.isDestroyed) return;
            try {
              getAiToolkit(editor).rejectAllSuggestions();
            } catch {
              // Editor may already be destroyed
            }
          });
        }
      }
      editorsRef.current = null;
      acceptStackRef.current = [];
      setAcceptCount(0);
      setSuggestions([]);
      updateIndex(0);
    },
    [updateIndex]
  );

  const {registerNavigationGuard} = useNavigationGuard();

  // Register navigation guard for guarding actions when suggestions are active
  const navigationGuardFn = useCallback((action) => {
    if (suggestionsRef.current.length > 0) {
      pendingActionRef.current = action;
      setExitDialogOpen(true);
    } else {
      action();
    }
  }, []);

  const navigationGuardFnRef = useRef(navigationGuardFn);
  navigationGuardFnRef.current = navigationGuardFn;

  useEffect(() => {
    return registerNavigationGuard((action) =>
      navigationGuardFnRef.current(action)
    );
  }, [registerNavigationGuard]);

  const handleRemoveFixes = useCallback(() => {
    rejectAll();
    setExitDialogOpen(false);
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    if (pending) pending();
  }, [rejectAll]);

  const handleContinueReview = useCallback(() => {
    pendingActionRef.current = null;
    setExitDialogOpen(false);
  }, []);

  // On unmount, reject all pending suggestions
  useEffect(() => {
    return () => {
      const editors = editorsRef.current;
      if (!editors) return;
      Object.values(editors).forEach((editor) => {
        if (!editor || editor.isDestroyed) return;
        try {
          getAiToolkit(editor).rejectAllSuggestions();
        } catch {
          // ignore
        }
      });
    };
  }, []);

  // Keep suggestions in sync with the toolkit on every doc change.
  // Also syncs currentIndex when user clicks a suggestion in the document.
  useEffect(() => {
    if (!hasActiveChanges) return;
    const editors = editorsRef.current;
    if (!editors) return;

    const active = Object.values(editors).filter((e) => e && !e.isDestroyed);

    const syncSuggestions = debounce(() => {
      const fresh = collectSuggestions(editorsRef.current || {});
      setSuggestions(fresh);

      // Clamp index if suggestions were removed
      if (fresh.length === 0) {
        updateIndex(0);
      } else if (currentIndexRef.current >= fresh.length) {
        updateIndex(fresh.length - 1);
      }
    }, 100);

    const handleTransaction = ({transaction}) => {
      if (!transaction.docChanged) return;
      syncSuggestions();
    };

    const handleClick = (editor) => (e) => {
      setTimeout(() => {
        try {
          const selected = getAiToolkit(editor).getSelectedSuggestion();
          if (!selected) return;

          // If click was on a "diff" widget, move caret to the suggestion range
          if (e.target.closest('.tiptap-ai-suggestion-diff')) {
            editor.commands.setTextSelection(selected.range.from);
            editor.commands.focus();
          }

          const idx = suggestionsRef.current.findIndex(
            (s) => s.id === selected.id
          );
          if (idx >= 0 && idx !== currentIndexRef.current) {
            updateIndex(idx);
          }
        } catch {
          // ignore
        }
      }, 0);
    };

    const clickHandlers = active.map((e) => {
      const handler = handleClick(e);
      e.view.dom.addEventListener('mousedown', handler, true);
      return {editor: e, handler};
    });
    active.forEach((e) => e.on('transaction', handleTransaction));

    return () => {
      syncSuggestions.cancel();
      clickHandlers.forEach(({editor: e, handler}) => {
        if (!e.isDestroyed) {
          e.view.dom.removeEventListener('mousedown', handler, true);
        }
      });
      active.forEach((e) => {
        if (!e.isDestroyed) e.off('transaction', handleTransaction);
      });
    };
  }, [hasActiveChanges, collectSuggestions, updateIndex]);

  // Escape key → show exit dialog
  useEffect(() => {
    if (!hasActiveChanges) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setExitDialogOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [hasActiveChanges]);

  // Block browser back/forward
  useEffect(() => {
    if (!hasActiveChanges) return;
    const handler = () => {
      window.history.pushState(null, '', window.location.href);
      setExitDialogOpen(true);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [hasActiveChanges]);

  // Warn before page unload
  useEffect(() => {
    if (!hasActiveChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasActiveChanges]);

  const value = useMemo(
    () => ({
      // State
      hasActiveChanges,
      suggestions,
      currentIndex,
      canUndo,

      // Navigation
      goToPrevious,
      goToNext,

      // Actions
      acceptCurrent,
      rejectCurrent,
      acceptAll,
      rejectAll,
      undoLastAction,

      // Lifecycle
      refreshSuggestions,
      clearSuggestions,
    }),
    [
      hasActiveChanges,
      suggestions,
      currentIndex,
      canUndo,
      goToPrevious,
      goToNext,
      acceptCurrent,
      rejectCurrent,
      acceptAll,
      rejectAll,
      undoLastAction,
      refreshSuggestions,
      clearSuggestions,
    ]
  );

  return (
    <SuggestionsContext.Provider value={value}>
      {children}
      <ExitProofreadingDialog
        open={exitDialogOpen}
        onClose={handleContinueReview}
        onRemoveFixes={handleRemoveFixes}
        remainingCount={suggestions.length}
      />
    </SuggestionsContext.Provider>
  );
};

SuggestionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const fn = () => {};
const SUGGESTIONS_DEFAULTS = {
  suggestions: [],
  currentIndex: 0,
  hasActiveChanges: false,
  acceptCount: 0,
  refreshSuggestions: fn,
  clearSuggestions: fn,
  goToNext: fn,
  goToPrevious: fn,
  acceptCurrent: fn,
  rejectCurrent: fn,
  acceptAll: fn,
  rejectAll: fn,
  undoLastAction: fn,
};

export const useSuggestions = () => {
  const context = useContext(SuggestionsContext);
  if (!context) return SUGGESTIONS_DEFAULTS;
  return context;
};

export default SuggestionsContext;
